// Generate distinct bell alarm tones via Web Audio API — no asset files needed.
// Each tone caps at ~8s total. Returns a stop() handle so the caller can
// terminate the sound when staff acknowledges.

let _audioCtx = null;
const getCtx = () => {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    if (_audioCtx.state === 'suspended') _audioCtx.resume();
    return _audioCtx;
  } catch (_) {
    return null;
  }
};

const beep = (ctx, freq, startAt, duration, gain = 0.45) => {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(gain, startAt + 0.02);
  g.gain.linearRampToValueAtTime(0, startAt + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
  return osc;
};

const sweep = (ctx, fromFreq, toFreq, startAt, duration, gain = 0.45) => {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sawtooth';
  osc.frequency.setValueAtTime(fromFreq, startAt);
  osc.frequency.exponentialRampToValueAtTime(Math.max(toFreq, 50), startAt + duration);
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(gain, startAt + 0.04);
  g.gain.linearRampToValueAtTime(0, startAt + duration);
  osc.connect(g);
  g.connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
  return osc;
};

const patterns = {
  // CBG → 3 short high beeps, repeats. Bright/clinical feel.
  cbg: (ctx, t0) => {
    const oscs = [];
    for (let rep = 0; rep < 4; rep++) {
      const base = t0 + rep * 1.6;
      oscs.push(beep(ctx, 1320, base + 0.0, 0.13));
      oscs.push(beep(ctx, 1320, base + 0.2, 0.13));
      oscs.push(beep(ctx, 1320, base + 0.4, 0.13));
    }
    return oscs;
  },
  // Temp → 2 medium beeps + 1 low. Repeats.
  temp: (ctx, t0) => {
    const oscs = [];
    for (let rep = 0; rep < 4; rep++) {
      const base = t0 + rep * 1.6;
      oscs.push(beep(ctx, 880, base + 0.0, 0.22));
      oscs.push(beep(ctx, 880, base + 0.3, 0.22));
      oscs.push(beep(ctx, 440, base + 0.7, 0.32));
    }
    return oscs;
  },
  // Attention → long descending sweep. Repeats with gap.
  attention: (ctx, t0) => {
    const oscs = [];
    for (let rep = 0; rep < 4; rep++) {
      const base = t0 + rep * 1.6;
      oscs.push(sweep(ctx, 1500, 360, base, 1.0, 0.5));
    }
    return oscs;
  },
};

export const playBellSound = (type) => {
  const ctx = getCtx();
  if (!ctx) return () => {};
  const t0 = ctx.currentTime;
  const pattern = patterns[type] || patterns.attention;
  const oscs = pattern(ctx, t0);
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    const now = ctx.currentTime;
    oscs.forEach((o) => {
      try { o.stop(now); } catch (_) { /* already stopped */ }
    });
  };
  // Hard cap at 8 seconds (per UX rule)
  setTimeout(stop, 8000);
  return stop;
};

export const BELL_LABEL = {
  cbg: 'CBG Check',
  temp: 'Temperature Check',
  attention: 'Attention Required',
};

export const BELL_ICON = {
  cbg: '🩸',
  temp: '🌡️',
  attention: '🛎️',
};
